export type DateTimeValue = Date | string | number;
export type DateTimeContext = 'date' | 'time' | 'date-time' | 'stamp' | 'log' | 'date-compact';

export interface FormatDateTimeOptions {
  context?: DateTimeContext;
  locale?: string | string[];
  timeZone?: string;
  invalidFallback?: string;
}

/** Returns the browser's preferred locale without forcing a product locale. */
export function browserLocale(): string | undefined {
  return typeof navigator !== 'undefined' && navigator.language
    ? navigator.language
    : undefined;
}

function asDate(value: DateTimeValue): Date {
  return value instanceof Date ? new Date(value.getTime()) : new Date(value);
}

function optionsForContext(
  context: DateTimeContext,
  timeZone?: string,
): Intl.DateTimeFormatOptions {
  const date: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const time: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  if (context === 'date') return { ...date, timeZone };
  // compact table cells: day + short month; the year joins only when the
  // caller passes it through (see formatDateTime's current-year check)
  if (context === 'date-compact') return { day: 'numeric', month: 'short', timeZone };
  if (context === 'time') return { ...time, timeZone };
  if (context === 'stamp') {
    return {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      ...time,
      timeZoneName: 'short',
      timeZone,
    };
  }
  if (context === 'log') return { ...date, ...time, second: '2-digit', timeZone };
  return { ...date, ...time, timeZone };
}

/**
 * Formats user-facing dates through one locale-aware policy.
 *
 * Supported display modes:
 * - `date`: unambiguous date with year.
 * - `time`: hour and minute.
 * - `date-time`: unambiguous date plus hour and minute (default).
 * - `stamp`: weekday, date, time, and time-zone name for live page chrome.
 * - `log`: date and time including seconds for explicit log contexts only.
 * - `date-compact`: day + short month for dense table cells; the year is
 *   added automatically when the date is not in the current year.
 */
export function formatDateTime(
  value: DateTimeValue,
  options: FormatDateTimeOptions = {},
): string {
  const date = asDate(value);
  if (Number.isNaN(date.getTime())) return options.invalidFallback ?? '—';

  const locale = options.locale ?? browserLocale();
  const context = options.context ?? 'date-time';
  const fmtOptions = optionsForContext(context, options.timeZone);
  if (context === 'date-compact' && date.getFullYear() !== new Date().getFullYear()) {
    fmtOptions.year = 'numeric';
  }
  return new Intl.DateTimeFormat(locale, fmtOptions).format(date);
}
